import { useEffect } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  LogOut, Boxes, LayoutDashboard, Settings, Wallet, BarChart3, ShoppingCart,
  Package, Users, Briefcase,
  Building2, Globe, UserCog, Shield, LayoutGrid, Flag, Hash,
  BookOpen, Users2, FileText, CreditCard, Receipt, Landmark, Coins, TrendingUp, BookMarked,
  Scale, LineChart, ClipboardList,
  FileSignature, ShoppingBag,
  Package2, Warehouse, Layers,
  Network, UserCircle, CalendarDays, Activity,
  Gauge, UserPlus, Building, Contact, Target, Columns, ListChecks, GitBranch,
  FolderKanban, LayoutList, ClipboardCheck, Sparkles, ScrollText, CheckSquare,
  Inbox, Mail,
  type LucideIcon,
} from 'lucide-react'
import { Bell, Bot, ShieldAlert } from 'lucide-react'
import { clearTokens } from '../lib/auth'
import { api } from '../lib/api'
import { useEnabledApps } from '../lib/useEnabledApps'
import NotificationsBell from './NotificationsBell'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'

type NavItem = { to: string; label: string; Icon: LucideIcon; end?: boolean; permission?: string }
type NavSection = {
  title: string | null
  Icon?: LucideIcon
  items: NavItem[]
  adminOnly?: boolean
  /** Module code (matches core.App.code). When the App row has is_enabled=false
   *  the whole section is hidden — for end users AND admins. */
  appCode?: string
}

const navSections: NavSection[] = [
  {
    title: null,
    Icon: LayoutDashboard,
    items: [{ to: '/admin', label: 'Dashboard', Icon: LayoutDashboard, end: true }],
    adminOnly: true,
  },
  {
    title: 'My Apps',
    Icon: Sparkles,
    // Visible to any user holding consulting.submit_time (Consultants AND
    // Contractors AND admins). Lives outside the appCode filter — these are
    // end-user self-service surfaces, not full admin modules.
    items: [
      { to: '/my-apps/work-report', label: 'Work Report', Icon: ScrollText, permission: 'consulting.submit_time' },
    ],
  },
  {
    title: 'Setup',
    Icon: Settings,
    adminOnly: true,
    items: [
      { to: '/admin/organization', label: 'Organization', Icon: Building2 },
      { to: '/admin/countries', label: 'Countries', Icon: Globe },
      { to: '/admin/users', label: 'Users', Icon: UserCog },
      { to: '/admin/invites', label: 'Invitations', Icon: Mail, permission: 'admin.invite_users' },
      { to: '/admin/email-settings', label: 'Email Settings', Icon: Mail, permission: 'admin.email_settings' },
      { to: '/admin/notifications/rules', label: 'Notification rules', Icon: Bell, permission: 'admin.notification_rules' },
      { to: '/admin/roles', label: 'Roles', Icon: Shield },
      { to: '/admin/apps', label: 'Modules', Icon: LayoutGrid },
      { to: '/admin/numbering', label: 'Numbering', Icon: Hash },
      { to: '/admin/ai-settings', label: 'AI Settings', Icon: Sparkles },
      { to: '/admin/document-templates', label: 'Document Templates', Icon: FileText },
      { to: '/admin/feature-flags', label: 'Feature flags', Icon: Flag },
    ],
  },
  {
    title: 'Partners',
    Icon: Users2,
    appCode: 'partners',
    items: [
      { to: '/admin/partners', label: 'Partners', Icon: Users2, permission: 'partners.access', end: true },
      { to: '/admin/partners/vendor-contracts', label: 'Vendor Contracts', Icon: ClipboardCheck, permission: 'partners.access' },
    ],
  },
  {
    title: 'Finance',
    Icon: Wallet,
    appCode: 'finance',
    items: [
      { to: '/admin/finance/accounts', label: 'Chart of Accounts', Icon: BookOpen, permission: 'finance.access' },
      { to: '/admin/finance/invoices', label: 'Invoices', Icon: FileText, permission: 'finance.access' },
      { to: '/admin/finance/payments', label: 'Payments', Icon: CreditCard, permission: 'finance.access' },
      { to: '/admin/finance/bills', label: 'Bills', Icon: Receipt, permission: 'finance.access' },
      { to: '/admin/finance/bank-rec', label: 'Bank Reconciliation', Icon: Landmark, permission: 'finance.access' },
      { to: '/admin/finance/currencies', label: 'Currencies', Icon: Coins, permission: 'finance.access' },
      { to: '/admin/finance/exchange-rates', label: 'Exchange Rates', Icon: TrendingUp, permission: 'finance.access' },
      { to: '/admin/finance/journal-entries', label: 'Journal Entries', Icon: BookMarked, permission: 'finance.access' },
    ],
  },
  {
    title: 'Finance Reports',
    Icon: BarChart3,
    appCode: 'finance',
    items: [
      { to: '/admin/finance/reports/trial-balance', label: 'Trial Balance', Icon: Scale, permission: 'finance.reports' },
      { to: '/admin/finance/reports/profit-loss', label: 'Profit & Loss', Icon: LineChart, permission: 'finance.reports' },
      { to: '/admin/finance/reports/balance-sheet', label: 'Balance Sheet', Icon: ClipboardList, permission: 'finance.reports' },
    ],
  },
  {
    title: 'Sales',
    Icon: ShoppingCart,
    appCode: 'sales',
    items: [
      { to: '/admin/sales/quotes', label: 'Quotes', Icon: FileSignature, permission: 'sales.access' },
      { to: '/admin/sales/orders', label: 'Sales Orders', Icon: ShoppingBag, permission: 'sales.access' },
    ],
  },
  {
    title: 'Procurement',
    Icon: Package2,
    appCode: 'procurement',
    items: [
      { to: '/admin/procurement/purchase-orders', label: 'Purchase Orders', Icon: ShoppingBag, permission: 'procurement.access' },
    ],
  },
  {
    title: 'Inventory',
    Icon: Package,
    appCode: 'inventory',
    items: [
      { to: '/admin/inventory/products', label: 'Products', Icon: Package2, permission: 'inventory.access' },
      { to: '/admin/inventory/warehouses', label: 'Warehouses', Icon: Warehouse, permission: 'inventory.access' },
      { to: '/admin/inventory/stock', label: 'Stock Levels', Icon: Layers, permission: 'inventory.access' },
    ],
  },
  {
    title: 'HR',
    Icon: Users,
    appCode: 'hr',
    items: [
      { to: '/admin/hr/departments', label: 'Departments', Icon: Network, permission: 'hr.access' },
      { to: '/admin/hr/job-profiles', label: 'Job Profiles', Icon: BookMarked, permission: 'hr.access' },
      { to: '/admin/hr/positions', label: 'Positions', Icon: GitBranch, permission: 'hr.access' },
      { to: '/admin/hr/people', label: 'People', Icon: Users2, permission: 'hr.access' },
      { to: '/admin/hr/employees', label: 'Employees', Icon: Briefcase, permission: 'hr.access' },
      { to: '/admin/hr/time-off', label: 'Time Off', Icon: CalendarDays, permission: 'hr.access' },
      { to: '/admin/hr/bench', label: 'Bench & Utilization', Icon: Activity, permission: 'hr.access' },
    ],
  },
  {
    title: 'CRM',
    Icon: Briefcase,
    appCode: 'crm',
    items: [
      { to: '/admin/crm/dashboard', label: 'Dashboard', Icon: Gauge, permission: 'crm.access' },
      { to: '/admin/crm/leads', label: 'Leads', Icon: UserPlus, permission: 'crm.access' },
      { to: '/admin/crm/accounts', label: 'Accounts', Icon: Building, permission: 'crm.access' },
      { to: '/admin/crm/contacts', label: 'Contacts', Icon: Contact, permission: 'crm.access' },
      { to: '/admin/crm/opportunities', label: 'Opportunities', Icon: Target, permission: 'crm.access' },
      { to: '/admin/crm/kanban', label: 'Pipeline (Kanban)', Icon: Columns, permission: 'crm.access' },
      { to: '/admin/crm/activities', label: 'Activities', Icon: ListChecks, permission: 'crm.access' },
      { to: '/admin/crm/pipelines', label: 'Pipelines & Stages', Icon: GitBranch, permission: 'crm.access' },
    ],
  },
  {
    title: 'Consulting',
    Icon: FolderKanban,
    appCode: 'consulting',
    items: [
      { to: '/admin/consulting/engagements', label: 'Engagements', Icon: FolderKanban, permission: 'consulting.access' },
      { to: '/admin/consulting/projects', label: 'Projects', Icon: LayoutList, permission: 'consulting.access' },
      { to: '/admin/consulting/consultants', label: 'Profiles', Icon: UserCircle, permission: 'consulting.access' },
      { to: '/admin/consulting/skills', label: 'Skill catalog', Icon: ListChecks, permission: 'consulting.admin' },
      { to: '/admin/consulting/approvals', label: 'Work Approvals', Icon: CheckSquare, permission: 'consulting.approve_time' },
    ],
  },
  {
    title: 'Recruitment',
    Icon: UserPlus,
    appCode: 'ats',
    items: [
      { to: '/admin/ats/jobs', label: 'Jobs', Icon: ClipboardList, permission: 'ats.access' },
      { to: '/admin/ats/candidates', label: 'Candidates', Icon: Contact, permission: 'ats.access' },
      { to: '/admin/ats/pipelines', label: 'Pipelines', Icon: Columns, permission: 'ats.write' },
    ],
  },
  {
    title: 'Requests',
    Icon: Inbox,
    appCode: 'requests',
    items: [
      { to: '/admin/requests', label: 'My Requests', Icon: Inbox, permission: 'requests.access', end: true },
      { to: '/admin/requests/inbox', label: 'Inbox', Icon: Bell, permission: 'requests.access' },
      { to: '/admin/requests/types', label: 'Catalog (admin)', Icon: Sparkles, permission: 'requests.admin' },
      { to: '/admin/requests/workflows', label: 'Workflows (admin)', Icon: GitBranch, permission: 'requests.admin' },
    ],
  },
  {
    title: 'AI Agents',
    Icon: Bot,
    items: [
      { to: '/admin/ai-agents', label: 'Agents', Icon: Bot, permission: 'admin.ai_agents' },
      { to: '/admin/ai-agents/runs', label: 'Runs', Icon: Sparkles, permission: 'admin.ai_agents' },
      { to: '/admin/ai-agents/approvals', label: 'Approvals', Icon: ShieldAlert, permission: 'admin.ai_agents' },
    ],
  },
]

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.get('/auth/me/')).data,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
  const isAdmin = !!(me?.is_staff || me?.is_superuser)
  const permissions: string[] = me?.permissions || []
  const username: string = me?.username || ''
  const email: string = me?.email || ''
  const personName: string = me?.person_name || ''
  const personId: number | null = me?.person_id || null
  const displayName = personName || username || 'User'
  const initial = (displayName || 'U').charAt(0).toUpperCase()

  const { isEnabled: isAppEnabled } = useEnabledApps()

  // Org branding for the sidebar header. Fails silently if the user
  // can't read /organization/ — defaults fall through to "MooderERP".
  const { data: org } = useQuery<{ group_name?: string; logo_url?: string; updated_at?: string } | undefined>({
    queryKey: ['organization'],
    queryFn: async () => (await api.get('/organization/')).data,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const orgName = (org?.group_name || '').trim() || 'MooderERP'
  // Backend returns logo_url as "/api/v1/organization/logo/" already. Append
  // a cache-buster (updated_at) so a fresh upload doesn't keep showing the
  // browser-cached old logo.
  const orgLogoUrl = org?.logo_url
    ? `${org.logo_url}${org.updated_at ? '?v=' + encodeURIComponent(org.updated_at) : ''}`
    : ''

  const handleLogout = () => {
    clearTokens()
    navigate(isAdmin ? '/admin/login' : '/login', { replace: true })
  }

  const visibleSections = navSections
    .map((section) => {
      if (section.adminOnly && !isAdmin) return null
      // Module sections (tagged with appCode) are hidden when the App row is
      // disabled — applies to admins too. The Setup section has no appCode so
      // it stays visible regardless and the admin can re-enable from there.
      if (section.appCode && !isAppEnabled(section.appCode)) return null
      const visibleItems = section.items.filter(
        (item) => !item.permission || isAdmin || permissions.includes(item.permission)
      )
      if (visibleItems.length === 0) return null
      return { ...section, items: visibleItems }
    })
    .filter(Boolean) as NavSection[]

  const isItemActive = (item: NavItem): boolean => {
    if (item.end) return location.pathname === item.to
    return location.pathname === item.to || location.pathname.startsWith(item.to + '/')
  }

  // Persist sidebar collapsed/expanded state across page navigations.
  // shadcn SidebarProvider writes the `sidebar_state` cookie on toggle but
  // doesn't read it back — every route change remounts AdminLayout and the
  // sidebar would otherwise reset to open. Read the cookie at mount and
  // pass it as defaultOpen so the user's last choice survives navigation.
  const sidebarDefaultOpen = (() => {
    if (typeof document === 'undefined') return true
    const m = document.cookie.match(/(?:^|;\s*)sidebar_state=(true|false)/)
    return m ? m[1] === 'true' : true
  })()

  // Persist sidebar scroll position across page navigations.
  // Same problem as the collapsed state above — AdminLayout remounts on every
  // route change, so the scroll container resets to top, which is annoying
  // when the user is working on a section far down the nav. Restore the
  // saved scrollTop after mount, and update sessionStorage on scroll
  // (rAF-debounced so we don't thrash storage).
  useEffect(() => {
    const el = document.querySelector<HTMLElement>('[data-sidebar="content"]')
    if (!el) return
    const KEY = 'sidebarScrollTop'
    const saved = Number(sessionStorage.getItem(KEY) || '0')
    if (saved > 0) {
      // Use rAF so the layout has actually settled before we scroll.
      requestAnimationFrame(() => { el.scrollTop = saved })
    }
    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        sessionStorage.setItem(KEY, String(el.scrollTop))
        ticking = false
      })
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <SidebarProvider defaultOpen={sidebarDefaultOpen}>
      <Sidebar collapsible="icon">
        <SidebarHeader>
  <SidebarMenu>
    <SidebarMenuItem>
      <SidebarMenuButton size="lg" tooltip={orgName} className="data-[slot=sidebar-menu-button]:!p-1.5">
        {/* Org logo if uploaded, else the generic Boxes icon. */}
        {orgLogoUrl ? (
          <img
            src={orgLogoUrl}
            alt={orgName}
            className="aspect-square size-8 rounded-md object-cover bg-sidebar-primary"
          />
        ) : (
          <div className="flex aspect-square size-8 items-center justify-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground">
            <Boxes className="size-4" />
          </div>
        )}
        <div className="grid flex-1 text-left text-sm leading-tight">
          {/* Org name as the sidebar title — falls back to MooderERP only
              when no Organization name has been set in Setup → Organization. */}
          <span className="truncate font-semibold">{orgName}</span>
        </div>
      </SidebarMenuButton>
    </SidebarMenuItem>
  </SidebarMenu>
</SidebarHeader>

        <SidebarContent>
          {visibleSections.map((section, i) => (
            <SidebarGroup key={i}>
              {section.title && (
  <SidebarGroupLabel>{section.title}</SidebarGroupLabel>
)}
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) => (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton asChild isActive={isItemActive(item)} tooltip={item.label}>
                        <NavLink to={item.to} end={item.end}>
                          <item.Icon />
                          <span>{item.label}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>

      </Sidebar>

      <SidebarInset>
        <header className="flex h-12 items-center gap-2 border-b border-border px-4 sticky top-0 bg-background z-10">
          <SidebarTrigger />
          <div className="text-sm text-muted-foreground flex-1">
            {/* breadcrumb / page-title slot for the future */}
          </div>

          {/* Notifications bell — sits left of the user menu. */}
          <NotificationsBell />

          {/* User menu — top-right anchor. Houses identity + sign-out so the
              sidebar stays focused purely on navigation. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-muted transition"
                aria-label="Open user menu"
              >
                <span className="h-7 w-7 rounded-full bg-primary/15 text-primary text-xs font-semibold inline-flex items-center justify-center">
                  {initial}
                </span>
                <span className="hidden sm:inline max-w-[14ch] truncate">{displayName}</span>
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel className="font-normal">
                <div className="text-sm font-medium truncate">{displayName}</div>
                {email && <div className="text-xs text-muted-foreground truncate">{email}</div>}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {personId && (
                <DropdownMenuItem onClick={() => navigate(`/admin/hr/people/${personId}`)}>
                  My profile
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate('/admin/requests')}>
                My requests
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/notifications')}>
                My notifications
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/me/notification-preferences')}>
                Notification preferences
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 overflow-auto">
          <div className="max-w-6xl mx-auto px-6 py-8">{children}</div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  )
}
